import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "jspdf-autotable";
import SliderWithLabel from "../../../components/SliderWithLabel";
import { useNavigate, useParams } from "react-router-dom";
import { MdArrowOutward } from "react-icons/md";
import { ChartCheckbox } from "./ChartCheckbox";
import { Box, Button, Typography } from "@mui/material";
import { usePortData } from "../../../contexts/PortDataContext";
import { getDeviceDetails } from "../../api/devices";

const chartDisplayNames = {
  thrust: "Thrust",
  ambientTemp: "Ambient Temperature",
  objectTemp: "Object Temperature",
  weight: "Weight",
  current: "Current",
  rpm: "RPM",
  dutyCycle: "Duty Cycle",
};

const ManualControl = () => {
  const { deviceId } = useParams(); 
  const [currentDevice, setCurrentDevice] = useState(null);
  const {
    globalPortData,
    selectedPort,
    setSelectedPort,
    availablePorts,
    serialStatus,
    isRunning,
    connectionMessage,
    refreshPorts,
    connectSerial,
    disconnectSerial,
    startThrust,
    stopThrust,
    sendThrottle,
  } = usePortData();
  const [localThrottle, setLocalThrottle] = useState(0);

  const [allChartsSelected, setAllChartsSelected] = useState(true);
  const [isRecording] = useState(false);
  const [escValue, setEscValue] = useState(1100);
  const [previousEscValue, setPreviousEscValue] = useState(1100);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState({
    thrust: [],
    ambientTemp: [],
    objectTemp: [],
    weight: [],
    current: [],
    rpm: [],
    dutyCycle: [],
  });

  const [selectedCharts, setSelectedCharts] = useState({
    thrust: true,
    ambientTemp: true,
    objectTemp: true,
    weight: true,
    current: true,
    rpm: true,
    dutyCycle: true,
  });

  useEffect(() => {
    const fetchDeviceDetails = async () => {
      try {
        setIsLoading(true);
        const deviceDetails = await getDeviceDetails(deviceId);
        setCurrentDevice(deviceDetails);
      } catch (error) {
        console.error("Error fetching device details:", error);
        setCurrentDevice({ id: deviceId });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeviceDetails();
  }, [deviceId]);

  const handleRestore = () => {
    handleEscValueChange(previousEscValue);
  };

  const handleClear = () => {
    handleEscValueChange(1100);
  };

  // NOTE: an IoT-telemetry effect used to sit here. It was removed because it
  // could never work and actively corrupted the live charts: nothing ever
  // subscribed to a telemetry topic (so its source was always empty), it
  // hardcoded `thrust: []` (the stand's headline measurement), and it did a
  // full setData() overwrite that wiped the serial stream's data every time
  // the throttle changed on an iotEnabled device. Live data comes from the
  // serial bridge below (PortDataContext → ws://…/ws).

  useEffect(() => {
    if (!globalPortData || globalPortData.type !== "data") return;
    const time = new Date(globalPortData.time).getTime();
    const append = (items, value) => [...items, { time, value }].slice(-30);

    setData((previous) => ({
      ...previous,
      thrust: append(previous.thrust, globalPortData.thrust),
      ambientTemp: append(previous.ambientTemp, globalPortData.ambientTemp),
      objectTemp: append(previous.objectTemp, globalPortData.objectTemp),
      weight: append(previous.weight, globalPortData.weight),
      current: append(previous.current, globalPortData.current),
      rpm: append(previous.rpm, globalPortData.rpm),
      dutyCycle: append(previous.dutyCycle, globalPortData.dutyCycle),
    }));
  }, [globalPortData]);

  const handleLocalThrottleChange = (event) => {
    const value = Number(event.target.value);
    setLocalThrottle(value);
    sendThrottle(value);
  };

  const handleStopThrust = () => {
    setLocalThrottle(0);
    stopThrust();
  };

  const handleEscValueChange = (newValue) => {
    setPreviousEscValue(escValue);
    setEscValue(newValue);
  };

  const renderLineChart = (title, label, dataKey, color) => (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center justify-center gap-x-2">
        <h3 className="text-[#EEEEEE] text-base font-light ml-14 text-center ">
          {title}
        </h3>
        <button
          onClick={() => navigate(`../charts/${dataKey}`)}
          className="text-[#EEEEEE] hover:text-[#306AF6] transition-colors duration-300"
        >
          <MdArrowOutward />
        </button>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data[dataKey]}>
          <Line type="monotone" dataKey="value" stroke={color} dot={false} />
          <CartesianGrid stroke="#ccc" />
          <XAxis
            dataKey="time"
            tickFormatter={(time) => new Date(time).toLocaleTimeString()}
          />
          <YAxis />
          <Tooltip />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  if (!currentDevice) {
    return <div>Loading device...</div>;
  }
  
  const localArduinoControls = (
    <Box sx={{ p: 3, m: 3, border: "1px solid #555", borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Local Arduino thrust stand</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Detected serial devices
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        {availablePorts.length === 0 ? (
          <Typography sx={{ py: 1 }}>No Arduino or COM device detected.</Typography>
        ) : availablePorts.map((port) => (
          <Button
            key={port.device}
            variant={selectedPort === port.device ? "contained" : "outlined"}
            color={selectedPort === port.device ? "info" : "inherit"}
            onClick={() => setSelectedPort(port.device)}
            disabled={serialStatus.connected || isRunning}
            sx={{
              minWidth: 220,
              justifyContent: "flex-start",
              textAlign: "left",
              textTransform: "none",
              px: 2,
              py: 1.25,
            }}
          >
            <Box component="span">
              <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
                {port.device}
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, opacity: 0.8 }}>
                {port.description || "Serial device"}
                {port.manufacturer ? ` - ${port.manufacturer}` : ""}
              </Box>
            </Box>
          </Button>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={() => refreshPorts()} disabled={isRunning}>
          Refresh ports
        </Button>
        {!serialStatus.connected ? (
          <Button
            variant="contained"
            onClick={() => connectSerial(selectedPort)}
            disabled={!selectedPort}
          >
            Connect Arduino
          </Button>
        ) : (
          <Button variant="outlined" color="warning" onClick={disconnectSerial} disabled={isRunning}>
            Disconnect Arduino
          </Button>
        )}
        <Button
          variant="contained"
          color={isRunning ? "error" : "success"}
          size="large"
          onClick={isRunning ? handleStopThrust : startThrust}
          disabled={!serialStatus.connected}
        >
          {isRunning ? "STOP THRUST" : "START THRUST"}
        </Button>
      </Box>
      <Box sx={{ mt: 3, maxWidth: 500 }}>
        <Typography>Throttle: {localThrottle}%</Typography>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={localThrottle}
          onChange={handleLocalThrottleChange}
          disabled={!isRunning}
          className="w-full"
        />
      </Box>
      <Typography sx={{ mt: 2 }} color={isRunning ? "success.main" : "text.secondary"}>
        {connectionMessage || "Start the local FastAPI service, then select the Arduino port."}
      </Typography>
    </Box>
  );

  // No serial link yet — the charts below have no source, so show the connect
  // controls and tell the user what to do.
  if (!serialStatus.connected) {
    return <>
      {localArduinoControls}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
          textAlign: 'center',
          p: 4,
          color: 'text.primary'
        }}
      >
        <Typography variant="h5" gutterBottom>
          Thrust stand not connected
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Start the local service, then select the Arduino port above to stream live data.
        </Typography>
      </Box>
    </>;
  }

  return (
    <div className="flex flex-col min-h-screen">
      {localArduinoControls}
      {/* Manual Control Section */}
      <div className="flex flex-col p-6 flex-shrink-0">
        {/* Setup Header */}
        <div className="mb-6">
          <p className="text-2xl text-[#EEEEEE] font-medium">Manual Control</p>
        </div>

        {/* Set Values */}
        <div className="grid grid-cols-6 gap-6 mb-16">
          <div className="col-span-4">
            <div className="mb-6">
              <p className="text-xl font-light text-[#EEEEEE] border-b-[1px] border-gray-600">
                Set Values
              </p>

              {/* SliderWithLabel */}
              <div className="mt-4 w-[40%] space-y-4">
                <SliderWithLabel
                  label="ESC :"
                  min={1100}
                  max={2000}
                  step={100}
                  onValueChange={handleEscValueChange}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-start gap-x-4">
              <button
                // onClick={handleRecordData}
                className="border-[1px] border-[#EEEEEE] px-8 py-1 rounded hover:bg-[#EEEEEE] hover:text-black text-[#EEEEEE] duration-300"
                disabled={isLoading}
              >
                {isRecording ? "Recording" : "Record Port Data"}
              </button>
              <button
                onClick={handleRestore}
                className="border-[1px] border-[#EEEEEE] px-8 py-1 rounded hover:bg-[#EEEEEE] hover:text-black text-[#EEEEEE] duration-300"
                disabled={isLoading}
              >
                Restore
              </button>
              <button
                onClick={handleClear}
                className="border-2 border-[#306AF6] px-8 py-1 rounded bg-[#306AF6] text-white hover:bg-[#1F316F] hover:border-[#1F316F] duration-300"
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
            <p className="text-white text-[12px] font-light mt-4">
              Note* Data will be recorded for 30s
            </p>
          </div>

          <div className="col-span-2 mt-7">
            <div className="flex items-start justify-start p-4 h-full text-justify text-[#EEEEEE] border-[1px] border-gray-600 rounded bg-[#1D1D1D]">
              <p className="font-thin text-sm">
                <strong className="text-red-600 font-bold">Warning: </strong>
                Wear safety goggles, keep away from rotating parts, and check
                fasteners are tight. Operating equipment beyond operating limits
                is a safety hazard. Do not operate unattended. Safety is the
                responsibility of the user.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-xl font-light text-[#EEEEEE] border-b-[1px] border-gray-600 mb-4">
            Select Charts to Display
          </p>
          <div className="flex gap-4">
            <ChartCheckbox
              label="All"
              checked={allChartsSelected}
              onChange={() => {
                setAllChartsSelected(!allChartsSelected);
                setSelectedCharts((prev) =>
                  Object.fromEntries(
                    Object.keys(prev).map((key) => [key, !allChartsSelected])
                  )
                );
              }}
              isAll={true}
            />
            {Object.entries(selectedCharts).map(([key, value]) => (
              <ChartCheckbox
                key={key}
                label={chartDisplayNames[key]}
                checked={value}
                onChange={() => {
                  setSelectedCharts((prev) => {
                    const newState = { ...prev, [key]: !prev[key] };
                    setAllChartsSelected(
                      Object.values(newState).every(Boolean)
                    );
                    return newState;
                  });
                }}
              />
            ))}
          </div>
        </div>

        {/* Graph Section */}
        <div className="grid grid-cols-3 gap-6">
          {Object.entries(selectedCharts).map(
            ([key, isSelected]) =>
              isSelected &&
              renderLineChart(
                chartDisplayNames[key],
                chartDisplayNames[key],
                key,
                "#FFC6C6"
              )
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualControl;
